import { Euler, Quaternion } from 'three'
import { Ease, type Easing } from './tween'

/**
 * Port of `Character` (TypeDefIndex 8569) — everything a passenger *does*.
 *
 * There is no Animator. `Character.SetAnimationState` decompiles to a bare
 * `return` (RVA 0x21156FC, four bytes), and the `IsWalking` / `Jump` / `Fall` /
 * `IdleStateHash` statics are never read. Every bit of motion a passenger has is
 * a DOTween sequence driving one of two euler fields:
 *
 *   `_walkWobbleEuler` (0x40) — the per-character wobble: walking, jumping,
 *   landing, settling. Killed and restarted by whichever animation runs last,
 *   because they all share `_jumpSequence` (0x30).
 *
 *   `_busSwayEuler` (0x5C) — the lean of a passenger riding a moving bus.
 *   Its own tween, id'd `"sway" + GetInstanceID()`, so it survives a wobble.
 *
 * and `SwayRotation` (0x2114934) multiplies them:
 *
 *     Quaternion.Euler(_busSwayEuler) * Quaternion.Euler(_walkWobbleEuler)
 *
 * which is what `BusSplineFollower.UpdatePassengerPoses` post-multiplies onto
 * the seat pose.
 *
 * ⚠️ "Nothing else reads either field" used to stand here, and it is wrong.
 * `_walkWobbleEuler` (0x40) has a second reader in `WalkAnimation`'s **own
 * setter**: `<WalkAnimation>b__20_1__0x2116b9c.c` is 0x100 bytes and ends in
 * `set_rotation_Injected`, so the walk wobble is written straight onto the
 * transform as a world rotation. Its landing-wobble twin
 * (`<PlayLandingWobble>b__22_1`, 0xc bytes) really does only store. See
 * `walkRotation`, and checklist §4b.
 *
 * The numbers below are the literals out of the decompiled sequences; only the
 * `SetEase` arguments are gone — see `WOBBLE_EASE`.
 */

/**
 * Every `DOTween.To` in `Character` is followed by `SetEase(...)`, and IL2CPP
 * dropped the argument in all of them. The shape of each sequence survives
 * (targets and durations), so this is the one thing being guessed, once, in one
 * place. `inOutQuad` is used rather than linear because a linear ±15° swing
 * reads as a mechanical triangle wave, which no shipped game ships.
 */
const WOBBLE_EASE: Easing = Ease.inOutQuad

interface Step {
  x: number
  y: number
  z: number
  seconds: number
}

/** One `DOTween.Sequence` of `Vector3` tweens chained onto a single euler. */
class EulerTrack {
  x = 0
  y = 0
  z = 0

  private steps: readonly Step[] = []
  private index = 0
  private elapsed = 0
  private fromX = 0
  private fromY = 0
  private fromZ = 0
  private loop = false

  get playing(): boolean {
    return this.index < this.steps.length
  }

  /** `Kill()` — stop where it stands, as DOTween does. */
  kill(): void {
    this.steps = []
    this.index = 0
    this.elapsed = 0
  }

  /** `Sequence()` + a run of `Append`. Each step starts from the previous value. */
  play(steps: readonly Step[], loop = false): void {
    this.steps = steps
    this.index = 0
    this.elapsed = 0
    this.loop = loop
    this.capture()
  }

  private capture(): void {
    this.fromX = this.x
    this.fromY = this.y
    this.fromZ = this.z
  }

  update(dt: number): void {
    let remaining = dt
    while (this.index < this.steps.length && remaining > 0) {
      const step = this.steps[this.index]
      const duration = Math.max(step.seconds, 1e-6)
      if (this.elapsed + remaining < duration) {
        this.elapsed += remaining
        remaining = 0
        const t = WOBBLE_EASE(this.elapsed / duration)
        this.x = this.fromX + (step.x - this.fromX) * t
        this.y = this.fromY + (step.y - this.fromY) * t
        this.z = this.fromZ + (step.z - this.fromZ) * t
        return
      }
      remaining -= duration - this.elapsed
      this.elapsed = 0
      this.x = step.x
      this.y = step.y
      this.z = step.z
      this.capture()
      this.index++
      if (this.index >= this.steps.length && this.loop) this.index = 0
    }
  }
}

/**
 * `Random.value > 0.5f ? 1f : -1f`, twice, from a seed.
 *
 * The original picks the wobble direction at random. A hash keeps replays and
 * the headless checks reproducible, which `Math.random` would not.
 */
function signs(seed: number): [number, number] {
  const a = hash01(seed * 2.0 + 11)
  const b = hash01(seed * 2.0 + 97)
  return [a > 0.5 ? 1 : -1, b > 0.5 ? 1 : -1]
}

/** Deterministic 0..1. */
export function hash01(n: number): number {
  const x = Math.sin(n * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

/** `Random.Range(min, max)`, deterministic. */
export function hashRange(seed: number, min: number, max: number): number {
  return min + hash01(seed) * (max - min)
}

const _euler = new Euler(0, 0, 0, 'YXZ')
const _sway = new Quaternion()
const _wobble = new Quaternion()

export class CharacterAnim {
  /** `_walkWobbleEuler` (0x40). */
  private readonly wobble = new EulerTrack()
  /** `_busSwayEuler` (0x5C). */
  private readonly sway = new EulerTrack()

  /**
   * A read-only look at `_walkWobbleEuler` and whether its tween is still
   * running. Nothing in the port needs this — it exists so `boarding.test.ts`
   * can check that `PlayLandingWobble` had the last word over
   * `ForceIdlePose`, without reaching past `private`.
   */
  walkWobbleState(): { x: number; y: number; z: number; playing: boolean } {
    const w = this.wobble
    return { x: w.x, y: w.y, z: w.z, playing: w.playing }
  }
  /**
   * The transform's own euler while a passenger is being thrown clear.
   *
   * Not a field of `Character` — `PlayCrashScatter` tweens the **transform**
   * with two `DORotate` calls, after zeroing both euler fields. So during a
   * scatter `SwayRotation` contributes nothing and this is the whole rotation.
   */
  private readonly crash = new EulerTrack()

  /**
   * Y rotation the crowd sim holds a passenger at — `CrowdEntry.BaseRotY`,
   * written by `<Receive>b__0` and read by `WaitingArea.LateUpdate`.
   */
  baseRotY = 0

  /**
   * Whether `StartBusMovingSway` has run without a matching stop.
   *
   * A bookkeeping field of this port's, not of `Character` — the original just
   * kills and rebuilds the tween, so it needs no such flag. It is kept because
   * it is the only handle from outside on which of the two sequences is
   * running, and `checkPassengers` and the sway tests both read it.
   */
  swaying = false

  /**
   * `WalkAnimation(direction)` — a looping side-to-side lean, 0.6 s a cycle.
   *
   *     _jumpSequence?.Kill()                                // FIRST
   *     if (direction.sqrMagnitude < 1e-10f) return          // then the guard
   *     _lookRotation = Quaternion.LookRotation(direction)   // never read again
   *     _walkWobbleEuler = Vector3.zero
   *     seq.Join  (To(_walkWobbleEuler, new Vector3(0, 0,  15f), 0.15f))
   *     seq.Append(To(_walkWobbleEuler, new Vector3(0, 0, -15f), 0.30f))
   *     seq.Append(To(_walkWobbleEuler, Vector3.zero,            0.15f))
   *     seq.SetLoops(-1)
   */
  walkAnimation(dx: number, dy: number, dz: number): void {
    // ⚠️ `Kill` comes **before** the guard in both setters
    // (`__0x2115814.c`:36-47, `__0x2116…`/`__0x2115c2c.c`:35-47 — the two
    // openings are byte-identical). The port had them the other way round, so a
    // zero-direction call was a no-op where the original stops whatever is
    // playing and leaves nothing running.
    //
    // 📐 Not reachable in play, and said so rather than implied: instrumenting
    // both setters over the 40 levels counted 39 590 walk and 19 513 jump calls
    // and **not one** with a zero direction. Fixed because the order is what the
    // decompile says, not because anything on screen moved.
    this.wobble.kill()
    if (dx * dx + dy * dy + dz * dz < 1e-10) return
    this.wobble.x = 0
    this.wobble.y = 0
    this.wobble.z = 0
    this.wobble.play(WALK, true)
  }

  /** `JumpAnimation(direction)` — the same shape at 0.1 s a leg. */
  jumpAnimation(dx: number, dy: number, dz: number): void {
    // Same order as `walkAnimation`, and for the same reason — see there.
    this.wobble.kill()
    if (dx * dx + dy * dy + dz * dz < 1e-10) return
    this.wobble.x = 0
    this.wobble.y = 0
    this.wobble.z = 0
    this.wobble.play(JUMP, true)
  }

  /**
   * `PlayLandingWobble()` — the one-shot bounce on touching down.
   *
   *     sx = Random.value > 0.5f ? 1f : -1f;  sz = likewise
   *     Append(To(new Vector3(sx *  12f, 0, sz *  12f), 0.12f))
   *     Append(To(new Vector3(sx *  -8f, 0, sz *  -8f), 0.15f))
   *     Append(To(new Vector3(sx *   4f, 0, sz *   4f), 0.12f))
   *     Append(To(Vector3.zero,                         0.12f))
   */
  playLandingWobble(seed: number): void {
    const [sx, sz] = signs(seed)
    this.wobble.kill()
    this.wobble.x = 0
    this.wobble.y = 0
    this.wobble.z = 0
    this.wobble.play(
      [
        { x: sx * 12, y: 0, z: sz * 12, seconds: 0.12 },
        { x: sx * -8, y: 0, z: sz * -8, seconds: 0.15 },
        { x: sx * 4, y: 0, z: sz * 4, seconds: 0.12 },
        { x: 0, y: 0, z: 0, seconds: 0.12 },
      ],
      false,
    )
  }

  /** `PlaySettleWobble()` — the softer version, on arriving in the waiting area. */
  playSettleWobble(seed: number): void {
    const [sx, sz] = signs(seed)
    this.wobble.kill()
    this.wobble.x = 0
    this.wobble.y = 0
    this.wobble.z = 0
    this.wobble.play(
      [
        { x: sx * 10, y: 0, z: sz * 10, seconds: 0.15 },
        { x: sx * -6, y: 0, z: sz * -6, seconds: 0.2 },
        { x: sx * 3, y: 0, z: sz * 3, seconds: 0.15 },
        { x: 0, y: 0, z: 0, seconds: 0.15 },
      ],
      false,
    )
  }

  /**
   * 🔍 `ForceIdlePose()` — kill the wobble sequence. That is all it does here.
   *
   *     _jumpSequence?.Kill()
   *     transform.localRotation =
   *         Quaternion.Euler(0f, transform.localEulerAngles.y, 0f)
   *
   * `ForceIdlePose__0x21142dc.c` never writes 0x40. Its twin does, and the
   * contrast is the whole point: `PlayLandingWobble__0x2116040.c` zeroes
   * `_walkWobbleEuler` immediately after its own `Kill()`, so the omission
   * here is deliberate, not an artefact of the decompile.
   *
   * This used to zero the track anyway, justified by "the lean lives in
   * `_walkWobbleEuler` here rather than on a transform, so zeroing the track is
   * the same thing". That reads well and is wrong: the second line of the
   * original stands the **transform** up, and the port's stand-in for the
   * transform is not this track — the crowd draws a settled member from
   * `crowdRotation`, which never looks at the wobble at all.
   *
   * Unobservable either way, and measured rather than assumed: over 10 levels
   * and 8 159 219 sampled rotations, `forceIdlePose` runs 7 772 times and
   * writing an absurd `wobble.x = 5` here changes nothing that is drawn. Both
   * call sites close the door — `session.attachPassenger` calls
   * `playLandingWobble` on the next line, which zeroes and replays the track,
   * and the waiting area sets `isJumping = false` first, after which
   * `memberRotation` reads `crowdRotation` instead of `walkRotation`.
   */
  forceIdlePose(): void {
    this.wobble.kill()
  }

  /**
   * `StartBusMovingSway()` — lean back and settle, for a passenger on a bus
   * that is moving. Damped about −20° of pitch:
   *
   *     DOTween.Kill("sway" + GetInstanceID())
   *     Append(To(_busSwayEuler, new Vector3(-30f, 0, 0), 0.20f))
   *     Append(To(_busSwayEuler, new Vector3( -5f, 0, 0), 0.25f))
   *     Append(To(_busSwayEuler, new Vector3(-25f, 0, 0), 0.20f))
   *     Append(To(_busSwayEuler, new Vector3(-20f, 0, 0), 0.20f))
   *
   * Called from `AttachPassenger` (when boarding a bus already rolling), from
   * `BusSplineFollower.LateUpdate` when a stopped bus starts again, and from
   * `DepartAndExit` for everyone aboard.
   */
  startBusMovingSway(): void {
    // No "already swaying" guard: the original opens with
    // `DOTween.Kill("sway" + GetInstanceID())` and builds a fresh sequence
    // every time, so a second call **restarts** the lean from wherever the
    // euler happens to sit. What keeps that from happening every frame is the
    // one-shot upstream — `_wasTrafficStopped` (0x15A) and the edge in
    // `SetHoldAtStop` — not anything here.
    this.swaying = true
    this.sway.kill()
    this.sway.play(SWAY_START, false)
  }

  /** `StopBusMovingSway()` — the mirror, back to upright through +30/−18/+10/−4. */
  stopBusMovingSway(): void {
    // Unguarded for the same reason `startBusMovingSway` is.
    this.swaying = false
    this.sway.kill()
    this.sway.play(SWAY_STOP, false)
  }

  /** Straight to upright, no tween — a passenger who was never on a bus. */
  clearSway(): void {
    this.swaying = false
    this.sway.kill()
    this.sway.x = 0
    this.sway.y = 0
    this.sway.z = 0
  }

  /**
   * `Character.PlayCrashScatter(landPosition, flightDuration)` — the rotation
   * half. 🔍 `Character$PlayCrashScatter__0x2115048.c`:
   *
   *     DOTween.Kill("sway" + GetInstanceID());
   *     _jumpSequence?.Kill();  <0x38>?.Kill();  transform.DOKill();
   *     _busSwayEuler = Vector3.zero;
   *     _walkWobbleEuler = Vector3.zero;
   *     transform.SetParent(null);
   *     ...
   *     seq.Join(transform.DORotate(new Vector3(xSpin, ySpin, zSpin), flight));
   *     seq.Join(transform.DORotate(new Vector3(faceUp, yEnd, 0f), flight * 0.35f));
   *
   * with, in the order the original draws them:
   *
   *     jumpPower = Random.Range(1.4f, 2.6f)      // the flight half
   *     signX     = Random.value                  // used below
   *     signZ     = Random.value
   *     xSpin     = Random.Range(240f, 520f)      // negated if signX <= 0.5
   *     ySpin     = Random.Range(-180f, 180f)
   *     zSpin     = Random.Range(240f, 520f)      // negated if signZ <= 0.5
   *     faceSign  = Random.value                  // +90 or -90
   *     yEnd      = Random.Range(0f, 360f)
   *
   * The draws are taken in that exact order so a seed maps to the same body
   * the original's RNG would have given it — see `hashRange`.
   *
   * ❓ Every `SetEase` argument is erased by IL2CPP, here as everywhere else in
   * `Character` (§4b). These are `Tweener`s, so unlike the `DOJump` sequence the
   * ease is **not** a no-op — the port uses `WOBBLE_EASE` like the rest.
   *
   * ❓ PLACEHOLDER on the **wrap** of the starting pose. `DORotate`'s default
   * `RotateMode.Fast` interpolates on `transform.eulerAngles`, which Unity
   * normalises to [0, 360) — so a rider leaning −20° starts the spin at 340 and
   * travels the long way round to a +300 target, where this port starts at −20
   * and takes the short way. The decomp cannot show it: the wrap happens inside
   * DOTween, not in the game's code. To settle it: a frame of
   * `reference/gameplay.mp4` at a crash, close enough to see which way a
   * passenger's first half-turn goes.
   */
  playCrashScatter(
    seed: number,
    flightDuration: number,
    startEuler: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 },
  ): void {
    // `transform.DOKill()` + both euler fields zeroed: nothing that was running
    // survives, and `SwayRotation` is identity from here on.
    this.swaying = false
    this.sway.kill()
    this.sway.x = this.sway.y = this.sway.z = 0
    this.wobble.kill()
    this.wobble.x = this.wobble.y = this.wobble.z = 0

    // 🔍 `DORotate` tweens **from the transform's current euler**, and zeroing
    // `_busSwayEuler`/`_walkWobbleEuler` does not touch that: those two are
    // inputs to `SwayRotation`, which only reaches the transform through
    // `BusSplineFollower.LateUpdate` — and `PlayFailCrash`:103 already did
    // `set_enabled(false)` on the follower. So the pose the spin starts from is
    // whatever the hierarchy was holding: the rider's seat rotation, their
    // sway, and however far the bus had rolled while carrying them (B7).
    //
    // This port used to start from a yaw-only pose, on the stated grounds that
    // the sway was identity and "only the yaw survives flattening". Neither
    // half held: riders do sway, and after B7 there is a large X/Z lean from
    // the roll as well.
    this.crash.kill()
    this.crash.x = startEuler.x
    this.crash.y = startEuler.y
    this.crash.z = startEuler.z

    const signX = hash01(seed * 3 + 17)
    const signZ = hash01(seed * 3 + 41)
    const xSpin = hashRange(seed * 3 + 59, 240, 520) * (signX <= 0.5 ? -1 : 1)
    const ySpin = hashRange(seed * 3 + 73, -180, 180)
    const zSpin = hashRange(seed * 3 + 97, 240, 520) * (signZ <= 0.5 ? -1 : 1)
    const faceUp = hash01(seed * 3 + 113) <= 0.5 ? -90 : 90
    const yEnd = hashRange(seed * 3 + 131, 0, 360)

    this.crash.play(
      [
        { x: xSpin, y: ySpin, z: zSpin, seconds: flightDuration },
        { x: faceUp, y: yEnd, z: 0, seconds: flightDuration * 0.35 },
      ],
      false,
    )
  }

  /**
   * The raw euler the crash track holds, in degrees.
   *
   * `crashRotation` composes it into a quaternion, and a `'YXZ'` decomposition
   * folds X back into [-90, 90] — which loses exactly the thing worth checking,
   * since the spin targets run to ±520°. Exposed so a test can hold the six
   * literals still without reaching into the track.
   */
  crashEuler(out = { x: 0, y: 0, z: 0 }): { x: number; y: number; z: number } {
    out.x = this.crash.x
    out.y = this.crash.y
    out.z = this.crash.z
    return out
  }

  /** The full local rotation of a passenger mid-scatter. */
  crashRotation(out = new Quaternion()): Quaternion {
    _euler.set(this.crash.x * DEG, this.crash.y * DEG, this.crash.z * DEG, 'YXZ')
    return out.setFromEuler(_euler)
  }

  update(dt: number): void {
    this.wobble.update(dt)
    this.sway.update(dt)
    this.crash.update(dt)
  }

  /**
   * `SwayRotation` — `Quaternion.Euler(_busSwayEuler) * Quaternion.Euler(
   * _walkWobbleEuler)`, in **Unity** space.
   *
   * Unity's `Quaternion.Euler` composes Y, then X, then Z, which is three's
   * `'YXZ'` order — the default `'XYZ'` would put the lean on the wrong axis
   * once a passenger is also turned.
   */
  swayRotation(out = new Quaternion()): Quaternion {
    _euler.set(this.sway.x * DEG, this.sway.y * DEG, this.sway.z * DEG, 'YXZ')
    _sway.setFromEuler(_euler)
    _euler.set(this.wobble.x * DEG, this.wobble.y * DEG, this.wobble.z * DEG, 'YXZ')
    _wobble.setFromEuler(_euler)
    return out.copy(_sway).multiply(_wobble)
  }

  /** The full local rotation of a passenger standing in a waiting area. */
  crowdRotation(tiltX: number, tiltZ: number, out = new Quaternion()): Quaternion {
    _euler.set(tiltX * DEG, this.baseRotY * DEG, tiltZ * DEG, 'YXZ')
    return out.setFromEuler(_euler)
  }

  /**
   * 🔍 What `WalkAnimation`'s own setter puts on the transform.
   *
   * `Character$$<WalkAnimation>b__20_1__0x2116b9c.c` is **0x100 bytes** and ends
   * in `set_rotation_Injected`: it writes `_walkWobbleEuler` (0x40/0x44/0x48)
   * and then `transform.rotation = Quaternion.Euler(that)`. Compare
   * `<PlayLandingWobble>b__22_1__0x21171e4.c`, which is **0xc bytes** and stores
   * the three floats and nothing else. So the walk wobble is drawn and the
   * landing wobble is not — the difference is visible in the function sizes
   * before it is visible in the bodies.
   *
   * `set_rotation`, not `set_localRotation`: this is a **world** rotation, and
   * it replaces the transform outright rather than composing with a parent. So
   * it must not be premultiplied by the room's rotation the way `crowdRotation`
   * is — while it is walking, the body simply faces the wobble's own euler.
   */
  walkRotation(out = new Quaternion()): Quaternion {
    _euler.set(this.wobble.x * DEG, this.wobble.y * DEG, this.wobble.z * DEG, 'YXZ')
    return out.setFromEuler(_euler)
  }
}

const DEG = Math.PI / 180

const WALK: readonly Step[] = [
  { x: 0, y: 0, z: 15, seconds: 0.15 },
  { x: 0, y: 0, z: -15, seconds: 0.3 },
  { x: 0, y: 0, z: 0, seconds: 0.15 },
]

const JUMP: readonly Step[] = [
  { x: 0, y: 0, z: 15, seconds: 0.1 },
  { x: 0, y: 0, z: -15, seconds: 0.1 },
  { x: 0, y: 0, z: 0, seconds: 0.1 },
]

const SWAY_START: readonly Step[] = [
  { x: -30, y: 0, z: 0, seconds: 0.2 },
  { x: -5, y: 0, z: 0, seconds: 0.25 },
  { x: -25, y: 0, z: 0, seconds: 0.2 },
  { x: -20, y: 0, z: 0, seconds: 0.2 },
]

const SWAY_STOP: readonly Step[] = [
  { x: 30, y: 0, z: 0, seconds: 0.2 },
  { x: -18, y: 0, z: 0, seconds: 0.25 },
  { x: 10, y: 0, z: 0, seconds: 0.2 },
  { x: -4, y: 0, z: 0, seconds: 0.15 },
  { x: 0, y: 0, z: 0, seconds: 0.15 },
]
